import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Button } from "../../design-system/primitives/Button";
import { Icon, type IconName } from "../../design-system/primitives/Icon";
import { Modal } from "../../design-system/primitives/Modal";
import { AxonMark } from "../../design-system/primitives/AxonMark";
import { cn } from "../../design-system/tokens";
import { useLangStore } from "../../store/langStore";
import { t as tr } from "../../lib/i18n";

const FEEDBACK_TYPES = ["incident", "idea", "contact"] as const;
type FeedbackType = (typeof FEEDBACK_TYPES)[number];

const TYPE_LABELS_ES: Record<FeedbackType, string> = {
  incident: "Incidencia",
  idea: "Idea de mejora",
  contact: "Contáctame",
};

// El slider va de 0 a 10. El `0` es el centinela de "sin calificacion".
const RATING_MIN = 0;
const RATING_MAX = 10;
const RATING_UNRATED = 0;

const RECAPTCHA_SITE_KEY = (
  import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? ""
).trim();

const FEEDBACK_ENDPOINT = `${import.meta.env.BASE_URL}feedback/index.php`;

const INPUT_CLASS =
  "w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-sm text-[var(--color-on-surface)] outline-none transition-colors placeholder:text-[var(--color-on-surface-variant)]/60 focus:border-[var(--color-brand)]";

interface FormState {
  name: string;
  email: string;
  type: FeedbackType;
  comment: string;
  rating: number;
}

const INITIAL_STATE: FormState = {
  name: "",
  email: "",
  type: "incident",
  comment: "",
  rating: RATING_UNRATED,
};

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const RECAPTCHA_SCRIPT_ID = "axon-recaptcha-v3";

/** Inyecta el script reCAPTCHA v3 una sola vez. */
function ensureRecaptcha(): Promise<void> {
  if (RECAPTCHA_SITE_KEY === "") {
    return Promise.resolve();
  }
  if (window.grecaptcha !== undefined) {
    return new Promise((resolve) => window.grecaptcha!.ready(resolve));
  }
  return new Promise((resolve, reject) => {
    if (document.getElementById(RECAPTCHA_SCRIPT_ID) !== null) {
      const interval = window.setInterval(() => {
        if (window.grecaptcha !== undefined) {
          window.clearInterval(interval);
          window.grecaptcha.ready(resolve);
        }
      }, 100);
      window.setTimeout(() => {
        window.clearInterval(interval);
        reject(new Error("recaptcha-load-timeout"));
      }, 8000);
      return;
    }
    const script = document.createElement("script");
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(RECAPTCHA_SITE_KEY)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.grecaptcha !== undefined) {
        window.grecaptcha.ready(resolve);
      } else {
        reject(new Error("recaptcha-not-defined"));
      }
    };
    script.onerror = () => reject(new Error("recaptcha-script-error"));
    document.head.appendChild(script);
  });
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Boton "Comentar" montado junto al icono About. Abre un dialogo con un
 * formulario de contacto y envia como JSON a `${BASE_URL}feedback/index.php`.
 */
export function FeedbackButton() {
  const { lang } = useLangStore();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<FormState>(INITIAL_STATE);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (state.name.trim() === "") e.name = tr("Este campo es obligatorio", lang);
    if (state.email.trim() === "") e.email = tr("Este campo es obligatorio", lang);
    else if (!isEmail(state.email)) e.email = tr("Ingresa un correo válido", lang);
    if (state.comment.trim() === "") e.comment = tr("Este campo es obligatorio", lang);
    return e;
  }, [state, lang]);

  const isInvalid = Object.keys(errors).length > 0;

  useEffect(() => {
    if (open) {
      setState(INITIAL_STATE);
      setTouched({});
      setStatus({ kind: "idle" });
    }
  }, [open]);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setTouched({ name: true, email: true, comment: true });
      if (isInvalid) {
        setStatus({
          kind: "error",
          message: tr("Revisa los campos marcados antes de enviar.", lang),
        });
        return;
      }
      setStatus({ kind: "submitting" });
      let captchaToken = "";
      try {
        await ensureRecaptcha();
        if (RECAPTCHA_SITE_KEY !== "" && window.grecaptcha !== undefined) {
          captchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
            action: "feedback_submit",
          });
        }
      } catch {
        setStatus({
          kind: "error",
          message: tr("Verificación de seguridad fallida. Recarga la página y vuelve a intentar.", lang),
        });
        return;
      }

      try {
        const response = await fetch(FEEDBACK_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: state.name.trim(),
            email: state.email.trim(),
            type: state.type,
            comment: state.comment.trim(),
            rating: state.rating,
            captcha_token: captchaToken,
            client_locale: navigator.language,
            client_timezone:
              Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
          }),
        });
        if (!response.ok) {
          if (response.status === 400) {
            setStatus({
              kind: "error",
              message: tr("Revisa los campos marcados antes de enviar.", lang),
            });
          } else if (response.status === 403) {
            setStatus({
              kind: "error",
              message: tr("Verificación de seguridad fallida. Recarga la página y vuelve a intentar.", lang),
            });
          } else {
            setStatus({
              kind: "error",
              message: tr("No fue posible enviar el mensaje. Intenta de nuevo en unos minutos.", lang),
            });
          }
          return;
        }
        setStatus({ kind: "success" });
      } catch {
        setStatus({
          kind: "error",
          message: tr("No hay conexión con el servidor. Revisa tu conexión e intenta de nuevo.", lang),
        });
      }
    },
    [isInvalid, state, lang],
  );

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        aria-label={tr("Enviar comentario o reportar incidencia", lang)}
        className="py-1 px-2 text-xs border-[var(--color-brand-border-60)] bg-[var(--color-brand-soft-10)] text-[var(--color-brand)] hover:bg-[var(--color-brand-soft-15)]"
      >
        <Icon name="comment" size={14} />
        {tr("Comentar", lang)}
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        ariaLabel={tr("Enviar comentario", lang)}
        widthClass="w-[min(560px,94vw)]"
      >
        <div className="flex flex-col gap-3 px-5 py-4">
          <header className="flex items-start gap-3 pr-8">
            <AxonMark size={28} />
            <div className="flex flex-col gap-0.5">
              <h2 className="text-base font-semibold text-[var(--color-on-surface)]">
                {tr("Enviar comentario", lang)}
              </h2>
              <p className="text-sm text-[var(--color-on-surface-variant)]">
                {tr("Cuéntanos tu experiencia, reporta una incidencia o sugiere una mejora. Tu mensaje llega directamente al equipo.", lang)}
              </p>
            </div>
          </header>

          {status.kind === "success" ? (
            <SuccessBanner
              message={tr("Mensaje enviado. Gracias por tu retroalimentación.", lang)}
              onClose={() => setOpen(false)}
            />
          ) : (
            <form className="flex flex-col gap-2.5" onSubmit={onSubmit}>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <Field
                  label={tr("Nombre", lang)}
                  error={touched.name ? errors.name : undefined}
                >
                  <input
                    autoComplete="name"
                    placeholder={tr("Tu nombre", lang)}
                    value={state.name}
                    onChange={(e) =>
                      setState((s) => ({ ...s, name: e.target.value }))
                    }
                    onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                    className={INPUT_CLASS}
                  />
                </Field>

                <Field
                  label={tr("Correo", lang)}
                  error={touched.email ? errors.email : undefined}
                >
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder={tr("tu@correo.com", lang)}
                    value={state.email}
                    onChange={(e) =>
                      setState((s) => ({ ...s, email: e.target.value }))
                    }
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    className={cn(INPUT_CLASS, "font-mono")}
                  />
                </Field>
              </div>

              <Field label={tr("Tipo", lang)}>
                <div className="flex flex-wrap gap-1.5">
                  {FEEDBACK_TYPES.map((type) => {
                    const isActive = state.type === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => setState((s) => ({ ...s, type }))}
                        className={cn(
                          "inline-flex h-7 items-center rounded border px-3 text-sm transition-colors focus:outline-none",
                          isActive
                            ? "border-[var(--color-brand-border-60)] bg-[var(--color-brand-soft-10)] text-[var(--color-brand)]"
                            : "border-[var(--color-outline-variant)] bg-[var(--color-surface-container-low)] text-[var(--color-on-surface-variant)] hover:border-[var(--color-border)] hover:text-[var(--color-on-surface)]",
                        )}
                      >
                        {tr(TYPE_LABELS_ES[type], lang)}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field
                label={tr("Comentario", lang)}
                error={touched.comment ? errors.comment : undefined}
              >
                <textarea
                  rows={3}
                  placeholder={tr("Describe la incidencia, comparte una idea o cuéntanos tu experiencia...", lang)}
                  value={state.comment}
                  onChange={(e) =>
                    setState((s) => ({ ...s, comment: e.target.value }))
                  }
                  onBlur={() => setTouched((t) => ({ ...t, comment: true }))}
                  className={cn(INPUT_CLASS, "resize-none")}
                />
              </Field>

              <RatingField
                value={state.rating}
                onChange={(rating) => setState((s) => ({ ...s, rating }))}
              />

              {status.kind === "error" && (
                <p className="rounded border border-[var(--color-status-absent)]/40 bg-[var(--color-status-absent)]/10 px-3 py-1.5 text-sm text-[var(--color-status-absent)]">
                  {status.message}
                </p>
              )}

              <div className="flex items-center justify-between gap-3 pt-1">
                <p className="text-xs text-[var(--color-on-surface-variant)]/80">
                  {tr("Tu nombre, correo y comentario se reenvían a un buzón privado del equipo Axon. No los compartimos con terceros.", lang)}
                </p>
                <div className="flex flex-shrink-0 gap-2">
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => setOpen(false)}
                    disabled={status.kind === "submitting"}
                  >
                    {tr("Cancelar", lang)}
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={status.kind === "submitting"}
                  >
                    <Icon name="send" size={14} />
                    {status.kind === "submitting" ? tr("Enviando…", lang) : tr("Enviar", lang)}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </Modal>
    </>
  );
}

interface FieldProps {
  label: string;
  error?: string;
  children: ReactNode;
}

function Field({ label, error, children }: FieldProps) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">
        {label}
      </span>
      {children}
      {error !== undefined && (
        <span className="text-sm text-[var(--color-status-absent)]">
          {error}
        </span>
      )}
    </label>
  );
}

interface RatingFieldProps {
  value: number;
  onChange: (next: number) => void;
}

function RatingField({ value, onChange }: RatingFieldProps) {
  const { lang } = useLangStore();
  const isUnrated = value === RATING_UNRATED;
  const { icon, color } = ratingFace(value);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">
          {tr("Calificación", lang)}
        </span>
        <span className="text-xs text-[var(--color-on-surface-variant)]/70">
          {tr("0 = sin calificación", lang)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Icon
          name="face_sad"
          size={16}
          className={
            isUnrated
              ? "text-[var(--color-on-surface-variant)]/40"
              : "text-[var(--color-status-absent)]"
          }
        />
        <input
          type="range"
          min={RATING_MIN}
          max={RATING_MAX}
          step={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-[var(--color-brand)]"
          aria-label={tr("Calificación", lang)}
        />
        <Icon
          name="face_happy"
          size={16}
          className={
            isUnrated
              ? "text-[var(--color-on-surface-variant)]/40"
              : "text-[var(--color-status-present)]"
          }
        />
        <div className="ml-1 flex w-24 items-center gap-1.5">
          <Icon name={icon} size={18} className={color} />
          {isUnrated ? (
            <span className="text-xs text-[var(--color-on-surface-variant)]/70">
              {tr("Sin nota", lang)}
            </span>
          ) : (
            <span className="font-mono text-sm text-[var(--color-brand)]">
              {`${value}/10`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ratingFace(value: number): { icon: IconName; color: string } {
  if (value === RATING_UNRATED) {
    return {
      icon: "face_neutral",
      color: "text-[var(--color-on-surface-variant)]/50",
    };
  }
  if (value <= 3) {
    return { icon: "face_sad", color: "text-[var(--color-status-absent)]" };
  }
  if (value <= 5) {
    return {
      icon: "face_neutral",
      color: "text-[var(--color-status-divergent)]",
    };
  }
  if (value <= 7) {
    return {
      icon: "face_neutral",
      color: "text-[var(--color-on-surface-variant)]",
    };
  }
  if (value <= 9) {
    return { icon: "face_happy", color: "text-[var(--color-brand)]" };
  }
  return { icon: "face_happy", color: "text-[var(--color-status-present)]" };
}

function SuccessBanner({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}) {
  const { lang } = useLangStore();
  return (
    <div className="flex flex-col gap-3 rounded-md border border-[var(--color-status-present)]/40 bg-[var(--color-status-present)]/10 p-4">
      <div className="flex items-start gap-3">
        <Icon
          name="check_circle"
          size={24}
          className="text-[var(--color-status-present)]"
        />
        <p className="text-sm text-[var(--color-on-surface)]">{message}</p>
      </div>
      <div className="flex justify-end">
        <Button variant="primary" onClick={onClose}>
          {tr("OK", lang)}
        </Button>
      </div>
    </div>
  );
}
